import React, {useMemo} from 'react';
import AdvancedOptions from '../../../../../components/slides/highlighted/question/components/advancedOptions/AdvancedOptions';
import ElementShadow from '../../../../../components/slides/highlighted/common/elementShadow';
import {COLOR_FIELDS} from '../../../../../components/slides/highlighted/common/fontAndBackgroundColorPicker';
import {FONT_SIZE, FontSize, IPresentationTheme, QuestionElement} from '../../../../../incase-domain/presentationTheme';
import {buildThemeQuestionPreviewElement} from '../buildThemeQuestionPreviewElement';

interface PresentationThemeQuestionElementProps {
    presentationTheme: IPresentationTheme;
    setPresentationTheme: React.Dispatch<React.SetStateAction<IPresentationTheme>>;
}

function toThemeFontSize(value: number, fontSizes: FontSize[]): FontSize {
    const matched = fontSizes.find((size) => Math.abs(size.value - value) < 0.001);
    return matched || {size: FONT_SIZE.MEDIUM, value};
}

export default function PresentationThemeQuestionElement(props: PresentationThemeQuestionElementProps): JSX.Element {
    const {presentationTheme, setPresentationTheme} = props;

    const mockElement = useMemo(() => buildThemeQuestionPreviewElement(presentationTheme), [presentationTheme]);

    type FontSizeField = 'questionFontSize' | 'fontSize' | 'commentFontSize';
    const FONT_SIZE_FIELD_TO_KEY: Record<FontSizeField, keyof QuestionElement> = {
        questionFontSize: 'questionFontSize',
        fontSize: 'answerFontSize',
        commentFontSize: 'commentFontSize',
    };
    const DIRECT_FIELDS = new Set<string>([
        'questionBackgroundColor',
        'questionBackgroundBorderRadius',
        'questionTextAlign',
        'fontFamily',
        'correctColor',
        'incorrectColor',
        'debatableColor',
        'correctCommentColor',
        'incorrectCommentColor',
        'debatableCommentColor',
        'borderRadius',
        'boxShadow',
    ]);

    const changeHandler = {
        single: (field: string, value: unknown): void => {
            setPresentationTheme((prevTheme) => {
                const prevQuestion: QuestionElement = prevTheme.elementStyles?.questionElement || {};
                const {fontSizes} = prevTheme;

                let patch: Partial<QuestionElement> | null = null;

                if (field in FONT_SIZE_FIELD_TO_KEY) {
                    const key = FONT_SIZE_FIELD_TO_KEY[field as FontSizeField];
                    patch = {[key]: toThemeFontSize(value as number, fontSizes)};
                } else if (DIRECT_FIELDS.has(field)) {
                    patch = {[field]: value};
                }

                if (!patch) return prevTheme;

                return {
                    ...prevTheme,
                    elementStyles: {
                        ...prevTheme.elementStyles,
                        questionElement: {...prevQuestion, ...patch},
                    },
                };
            });
        },
        multiple: (fields: string[], values: unknown[]): void => {
            fields.forEach((field, index) => {
                changeHandler.single(field, values[index]);
            });
        },
    };

    return (
        <>
            <AdvancedOptions
                highlightedElement={mockElement}
                highlightedElementChangeHandler={changeHandler}
                answerColorFields={[COLOR_FIELDS.CORRECT_COLOR, COLOR_FIELDS.INCORRECT_COLOR, COLOR_FIELDS.DEBATABLE_COLOR]}
                commentColorFields={[
                    COLOR_FIELDS.CORRECT_COMMENT_COLOR,
                    COLOR_FIELDS.INCORRECT_COMMENT_COLOR,
                    COLOR_FIELDS.DEBATABLE_COMMENT_COLOR,
                ]}
            />
            <ElementShadow highlightedElement={mockElement} highlightedElementChangeHandler={changeHandler} />
        </>
    );
}
